import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { useWebSocket } from '../context/WebSocketContext';
import { ordersAPI, roomsAPI, usersAPI, workSchedulesAPI } from '../services/api';
import { USER_ROLES } from '../utils/constants';
import { formatDate, getTodayFormatted, getStatusLabel, getStatusColor } from '../utils/helpers';
import {
  FiClipboard,
  FiUsers,
  FiKey,
  FiBarChart,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiGrid
} from 'react-icons/fi';
import { Skeleton, SkeletonCard } from '../design-system';
import LoadingSpinner from '../components/common/LoadingSpinner';
import RoomStatusOverview from '../components/dashboard/RoomStatusOverview';
import StaffCleaningOrdersView from '../components/dashboard/StaffCleaningOrdersView';
import TimeClockButton from '../components/timeEntry/TimeClockButton';
import TimeEntryAlerts from '../components/dashboard/TimeEntryAlerts';

const Dashboard = () => {
  const { user } = useAuth();
  const { isMobile, addNotification } = useApp();
  const { subscribe } = useWebSocket();
  const [selectedDate, setSelectedDate] = useState(getTodayFormatted());
  const [stats, setStats] = useState({
    totalOrders: 0,
    completedOrders: 0,
    pendingOrders: 0,
    totalRooms: 0,
    cleanRooms: 0,
    dirtyRooms: 0,
    totalUsers: 0,
    activeUsers: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    rooms: [],
    orders: [],
    users: [],
    schedules: []
  });
  // Cache para datos prefetched
  const [dataCache, setDataCache] = useState({});

  // Función helper para calcular fecha siguiente/anterior
  const getAdjacentDate = (dateStr, daysOffset) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + daysOffset);
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    loadDashboardData();
  }, [user, selectedDate]);

  // WebSocket event handler - Update data incrementally without full reload
  const handleOrderUpdate = useCallback((orderData) => {

    // Update orders in state
    setDashboardData(prevData => {
      const updatedOrders = [...prevData.orders];
      const existingOrderIndex = updatedOrders.findIndex(o => o.id === orderData.id);

      if (existingOrderIndex >= 0) {
        // Update existing order
        updatedOrders[existingOrderIndex] = orderData;
      } else {
        // Add new order
        updatedOrders.push(orderData);
      }

      // Recalculate stats based on updated orders
      const completedCount = updatedOrders.filter(o => o.cleaning_timestamp !== null).length;
      const pendingCount = updatedOrders.filter(o => o.cleaning_timestamp === null).length;

      setStats(prevStats => ({
        ...prevStats,
        totalOrders: updatedOrders.length,
        completedOrders: completedCount,
        pendingOrders: pendingCount,
      }));

      // Update recent orders
      setRecentOrders(updatedOrders.slice(0, 5));

      return {
        ...prevData,
        orders: updatedOrders
      };
    });
  }, []);

  // Subscribe to WebSocket events
  useEffect(() => {
    const unsubscribeCreated = subscribe('order_created', handleOrderUpdate);
    const unsubscribeUpdated = subscribe('order_updated', handleOrderUpdate);
    const unsubscribeCheckoutCompleted = subscribe('checkout_completed', handleOrderUpdate);
    const unsubscribeCheckoutUnmarked = subscribe('checkout_unmarked', handleOrderUpdate);
    const unsubscribeCleaningCompleted = subscribe('cleaning_completed', handleOrderUpdate);
    const unsubscribeCleaningUnmarked = subscribe('cleaning_unmarked', handleOrderUpdate);

    return () => {
      unsubscribeCreated();
      unsubscribeUpdated();
      unsubscribeCheckoutCompleted();
      unsubscribeCheckoutUnmarked();
      unsubscribeCleaningCompleted();
      unsubscribeCleaningUnmarked();
    };
  }, [subscribe, handleOrderUpdate]);

  const loadDashboardData = async () => {
    try {
      // Verificar si tenemos datos en cache
      if (dataCache[selectedDate]) {
        // Usar datos del cache inmediatamente
        const cachedData = dataCache[selectedDate];
        setDashboardData(cachedData.dashboardData);
        setStats(cachedData.stats);
        setRecentOrders(cachedData.recentOrders);

        // No mostrar loading si tenemos cache
        if (isInitialLoad) {
          setIsLoadingData(false);
          setIsInitialLoad(false);
        }

        // Prefetch días adyacentes después de cargar desde cache
        setTimeout(() => prefetchAdjacentDays(selectedDate), 100);
        return;
      }

      // Mostrar spinner de pantalla completa solo en carga inicial
      if (isInitialLoad) {
        setIsLoadingData(true);
      } else {
        // Mostrar spinner sutil durante recarga
        setIsRefreshing(true);
      }

      // Load different data based on user role
      if (user?.role === USER_ROLES.CLEANING_STAFF) {
        await loadCleaningStaffData(selectedDate);
      } else {
        await loadAdminData(selectedDate);
      }

      // Prefetch días adyacentes después de cargar el día actual
      setTimeout(() => prefetchAdjacentDays(selectedDate), 500);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      addNotification({
        type: 'error',
        message: 'Error al cargar los datos del dashboard'
      });
    } finally {
      if (isInitialLoad) {
        setIsLoadingData(false);
        setIsInitialLoad(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  const loadCleaningStaffData = async (date) => {
    try {
      const orders = await ordersAPI.getMyOrders(date);
      const recentOrdersData = orders.slice(0, 5);
      const statsData = {
        totalOrders: orders.length,
        completedOrders: orders.filter(o => o.cleaning_timestamp !== null).length,
        pendingOrders: orders.filter(o => o.cleaning_timestamp === null).length,
        totalRooms: 0,
        cleanRooms: 0,
        dirtyRooms: 0,
        totalUsers: 0,
        activeUsers: 0,
      };

      setRecentOrders(recentOrdersData);
      setStats(statsData);

      // Guardar en cache
      setDataCache(prev => ({
        ...prev,
        [date]: {
          dashboardData: { rooms: [], orders, users: [], schedules: [] },
          stats: statsData,
          recentOrders: recentOrdersData
        }
      }));
    } catch (error) {
      console.error('Error loading cleaning staff data:', error);
    }
  };

  const loadAdminData = async (date) => {
    try {
      const [ordersResponse, roomsResponse, usersResponse, schedulesResponse] = await Promise.all([
        ordersAPI.getByDate(date),
        roomsAPI.getAll(),
        usersAPI.getAll(),
        workSchedulesAPI.getAll({ start_date: date, end_date: date })
      ]);

      const recentOrdersData = ordersResponse.slice(0, 5);
      const dashboardDataObj = {
        rooms: roomsResponse,
        orders: ordersResponse,
        users: usersResponse,
        schedules: schedulesResponse
      };
      const statsData = {
        totalOrders: ordersResponse.length,
        completedOrders: ordersResponse.filter(o => o.cleaning_timestamp !== null).length,
        pendingOrders: ordersResponse.filter(o => o.cleaning_timestamp === null).length,
        totalRooms: roomsResponse.length,
        cleanRooms: roomsResponse.filter(r => r.status === 'clean').length,
        dirtyRooms: roomsResponse.filter(r => r.status === 'dirty').length,
        totalUsers: usersResponse.length,
        activeUsers: usersResponse.filter(u => u.is_active).length,
      };

      setRecentOrders(recentOrdersData);
      setDashboardData(dashboardDataObj);
      setStats(statsData);

      // Guardar en cache
      setDataCache(prev => ({
        ...prev,
        [date]: {
          dashboardData: dashboardDataObj,
          stats: statsData,
          recentOrders: recentOrdersData
        }
      }));
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  };

  // Función para limpiar cache antiguo (mantener solo día actual y adyacentes)
  const cleanOldCache = (currentDate) => {
    const previousDate = getAdjacentDate(currentDate, -1);
    const nextDate = getAdjacentDate(currentDate, 1);
    const allowedDates = [previousDate, currentDate, nextDate];

    setDataCache(prev => {
      const newCache = {};
      let removedCount = 0;

      allowedDates.forEach(date => {
        if (prev[date]) {
          newCache[date] = prev[date];
        }
      });

      removedCount = Object.keys(prev).length - Object.keys(newCache).length;

      if (removedCount > 0) {
        console.log(`🗑️ Eliminados ${removedCount} días del cache`);
      }

      return newCache;
    });
  };

  // Función para precargar días adyacentes
  const prefetchAdjacentDays = async (currentDate) => {
    // Primero limpiar cache antiguo
    cleanOldCache(currentDate);

    const previousDate = getAdjacentDate(currentDate, -1);
    const nextDate = getAdjacentDate(currentDate, 1);

    // Precargar solo si no están ya en cache
    const datesToPrefetch = [previousDate, nextDate].filter(date => !dataCache[date]);

    if (datesToPrefetch.length === 0) {
      console.log('✅ Días adyacentes ya están en cache');
      return;
    }

    console.log('🔄 Precargando días adyacentes:', datesToPrefetch);

    // Precargar en paralelo
    await Promise.all(
      datesToPrefetch.map(async (date) => {
        try {
          if (user?.role === USER_ROLES.CLEANING_STAFF) {
            const orders = await ordersAPI.getMyOrders(date);
            const recentOrdersData = orders.slice(0, 5);
            const statsData = {
              totalOrders: orders.length,
              completedOrders: orders.filter(o => o.cleaning_timestamp !== null).length,
              pendingOrders: orders.filter(o => o.cleaning_timestamp === null).length,
              totalRooms: 0,
              cleanRooms: 0,
              dirtyRooms: 0,
              totalUsers: 0,
              activeUsers: 0,
            };

            setDataCache(prev => ({
              ...prev,
              [date]: {
                dashboardData: { rooms: [], orders, users: [], schedules: [] },
                stats: statsData,
                recentOrders: recentOrdersData
              }
            }));
          } else {
            const [ordersResponse, roomsResponse, usersResponse, schedulesResponse] = await Promise.all([
              ordersAPI.getByDate(date),
              roomsAPI.getAll(),
              usersAPI.getAll(),
              workSchedulesAPI.getAll({ start_date: date, end_date: date })
            ]);

            const recentOrdersData = ordersResponse.slice(0, 5);
            const dashboardDataObj = {
              rooms: roomsResponse,
              orders: ordersResponse,
              users: usersResponse,
              schedules: schedulesResponse
            };
            const statsData = {
              totalOrders: ordersResponse.length,
              completedOrders: ordersResponse.filter(o => o.cleaning_timestamp !== null).length,
              pendingOrders: ordersResponse.filter(o => o.cleaning_timestamp === null).length,
              totalRooms: roomsResponse.length,
              cleanRooms: roomsResponse.filter(r => r.status === 'clean').length,
              dirtyRooms: roomsResponse.filter(r => r.status === 'dirty').length,
              totalUsers: usersResponse.length,
              activeUsers: usersResponse.filter(u => u.is_active).length,
            };

            setDataCache(prev => ({
              ...prev,
              [date]: {
                dashboardData: dashboardDataObj,
                stats: statsData,
                recentOrders: recentOrdersData
              }
            }));
          }
          console.log('✅ Precargado exitoso para', date);
        } catch (error) {
          console.error('❌ Error precargando', date, error);
        }
      })
    );
  };

  const getStatsCards = () => {
    if (user?.role === USER_ROLES.CLEANING_STAFF) {
      return [
        {
          name: 'Órdenes de Hoy',
          value: stats.totalOrders,
          icon: FiClipboard,
          color: 'text-blue-600 bg-blue-100',
        },
        {
          name: 'Completadas',
          value: stats.completedOrders,
          icon: FiCheckCircle,
          color: 'text-green-600 bg-green-100',
        },
        {
          name: 'Pendientes',
          value: stats.pendingOrders,
          icon: FiClock,
          color: 'text-yellow-600 bg-yellow-100',
        },
      ];
    }

    return [
      {
        name: 'Órdenes de Hoy',
        value: stats.totalOrders,
        icon: FiClipboard,
        color: 'text-blue-600 bg-blue-100',
      },
      {
        name: 'Completadas',
        value: stats.completedOrders,
        icon: FiCheckCircle,
        color: 'text-green-600 bg-green-100',
      },
      {
        name: 'Pendientes',
        value: stats.pendingOrders,
        icon: FiClock,
        color: 'text-yellow-600 bg-yellow-100',
      },
      {
        name: 'Total Habitaciones',
        value: stats.totalRooms,
        icon: FiKey,
        color: 'text-indigo-600 bg-indigo-100',
      },
      {
        name: 'Habitaciones Limpias',
        value: stats.cleanRooms,
        icon: FiCheckCircle,
        color: 'text-green-600 bg-green-100',
      },
      {
        name: 'Habitaciones Sucias',
        value: stats.dirtyRooms,
        icon: FiAlertCircle,
        color: 'text-red-600 bg-red-100',
      },
    ];
  };

  const statsCards = getStatsCards();
  const numberOfStatsCards = user?.role === USER_ROLES.CLEANING_STAFF ? 3 : 6;

  return (
    <div className="space-y-6">
      {(isLoadingData || isRefreshing) ? (
          <>
            {/* Skeleton for Time Clock Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <Skeleton variant="text" width="150px" height="24px" className="bg-blue-500/50" />
                  <Skeleton variant="text" width="180px" height="16px" className="mt-2 bg-blue-500/50" />
                </div>
                <Skeleton variant="rect" width="100px" height="40px" className="bg-blue-500/50 rounded-lg" />
              </div>
            </div>

            {/* Skeleton for Welcome Header + Stats Cards */}
            {user?.role !== USER_ROLES.RECEPTIONIST && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className={`flex ${isMobile ? 'flex-col' : 'flex-row items-center justify-between'} gap-6`}>
                  {/* Welcome Text Skeleton */}
                  <div className={isMobile ? '' : 'flex-shrink-0'}>
                    <Skeleton variant="text" width="300px" height="32px" />
                    <Skeleton variant="text" width="250px" height="20px" className="mt-2" />
                  </div>

                  {/* Stats Cards Skeleton */}
                  <div className={`flex flex-row ${isMobile ? 'flex-wrap' : ''} gap-3 ${isMobile ? '' : 'flex-1 justify-end'}`}>
                    {Array.from({ length: numberOfStatsCards }, (_, i) => (
                      <div key={i} className={`bg-gray-50 rounded-lg p-4 ${isMobile ? 'flex-[0_0_calc(50%-0.375rem)]' : ''} min-w-0`}>
                        <div className="flex items-center justify-center lg:justify-start">
                          <Skeleton variant="circle" width="36px" height="36px" />
                          <div className="ml-3 hidden lg:block space-y-2">
                            <Skeleton variant="text" width="80px" height="12px" />
                            <Skeleton variant="text" width="40px" height="24px" />
                          </div>
                          <div className="ml-2 lg:hidden">
                            <Skeleton variant="text" width="40px" height="24px" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Skeleton for Room Status Overview and Orders */}
            <SkeletonCard hasImage={false} hasFooter={false} className="min-h-[400px]" />
            <SkeletonCard hasImage={false} hasFooter={false} className="min-h-[300px]" />
          </>
        ) : (
          <>
            {/* Time Entry Alerts - For Admins */}
            <TimeEntryAlerts />

            {/* Time Clock Section - For All Users */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-white">Control de Fichajes</h2>
                  <p className="text-sm text-blue-100">Registra tu entrada y salida</p>
                </div>
                <TimeClockButton />
              </div>
            </div>

            {/* Receptionist view - Room Status and Staff Orders */}
            {user?.role === USER_ROLES.RECEPTIONIST ? (
              <>
                {/* Room Status Overview - For Receptionist */}
                {dashboardData.rooms.length > 0 && (
                  <RoomStatusOverview
                    rooms={dashboardData.rooms}
                    orders={dashboardData.orders}
                    users={dashboardData.users}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                  />
                )}

                {/* Staff Cleaning Orders View - For Receptionist */}
                {dashboardData.orders.length > 0 && (
                  <StaffCleaningOrdersView
                    orders={dashboardData.orders}
                    users={dashboardData.users}
                    rooms={dashboardData.rooms}
                    schedules={dashboardData.schedules}
                  />
                )}
              </>
            ) : (
              <>
                {/* Welcome Header + Stats Cards */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className={`flex ${isMobile ? 'flex-col' : 'flex-row items-center justify-between'} gap-6`}>
                    {/* Welcome Text */}
                    <div className={isMobile ? '' : 'flex-shrink-0'}>
                      <h1 className="text-2xl font-bold text-gray-900">
                        ¡Bienvenido, {user?.name || user?.username}!
                      </h1>
                      <p className="text-gray-600 mt-1">
                        {formatDate(new Date(selectedDate))} - {user?.establishment?.name || 'Dashboard'}
                      </p>
                    </div>

                    {/* Stats Cards */}
                    <div className={`flex flex-row ${isMobile ? 'flex-wrap' : ''} gap-3 ${isMobile ? '' : 'flex-1 justify-end'}`}>
                      {statsCards.map((stat) => (
                        <div key={stat.name} className={`bg-gray-50 rounded-lg p-4 ${isMobile ? 'flex-[0_0_calc(50%-0.375rem)]' : ''} min-w-0`}>
                          <div className="flex items-center justify-center lg:justify-start">
                            <div className={`flex-shrink-0 rounded-md p-2 ${stat.color}`}>
                              <stat.icon className="h-5 w-5" />
                            </div>
                            <div className="ml-3 hidden lg:block">
                              <p className="text-xs font-medium text-gray-600">{stat.name}</p>
                              <p className="text-xl font-semibold text-gray-900">{stat.value}</p>
                            </div>
                            <div className="ml-2 lg:hidden text-center">
                              <p className="text-xl font-semibold text-gray-900">{stat.value}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Room Status Overview - Only for Admin */}
                {(user?.role === USER_ROLES.PLATFORM_ADMIN ||
                  user?.role === USER_ROLES.ESTABLISHMENT_ADMIN) && dashboardData.rooms.length > 0 && (
                  <RoomStatusOverview
                    rooms={dashboardData.rooms}
                    orders={dashboardData.orders}
                    users={dashboardData.users}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                  />
                )}

                {/* Staff Cleaning Orders View - Only for Admin */}
                {(user?.role === USER_ROLES.PLATFORM_ADMIN ||
                  user?.role === USER_ROLES.ESTABLISHMENT_ADMIN) && dashboardData.orders.length > 0 && (
                  <StaffCleaningOrdersView
                    orders={dashboardData.orders}
                    users={dashboardData.users}
                    rooms={dashboardData.rooms}
                    schedules={dashboardData.schedules}
                  />
                )}
              </>
            )}
          </>
        )}
    </div>
  );
};

export default Dashboard;